/**
 * The dish sheet: one dish's choices, over the menu.
 *
 * The list keeps the dish, its description and its price; everything that can be
 * chosen lives here. That is the whole reason it exists — a dish with two groups
 * of fifteen choices was drawing thirty-one chips into the menu, and the price
 * was the only thing saying which of them cost money.
 *
 * It is the shared `Modal`, so the title, the close control, the grabber, the
 * backdrop and the keyboard handling are the ones every other sheet in the app
 * already has. What this file owns is the body and the footer.
 *
 * The type is the app's own scale, which is the cart's: a thing and its price
 * are 16 and 14 — a chosen line in the cart is `text-base font-semibold` with
 * `text-sm` under it — headings are 16 bold, values are 18 bold, and 12 is kept
 * for footnotes. A choice is the thing being read here, so it is the 16, not
 * the 14 a summary of it would be.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Check } from 'lucide-react-native';
import {
  formatMoney,
  withOptions,
  type CartOption,
  type PublicMenuItem,
  type PublicOptionGroup,
} from '@yummy/shared';
import {
  Button,
  Icon,
  Modal,
  Row,
  Stack,
  Stepper,
  Text,
  ToneBadge,
  cn,
  useResponsive,
  useT,
} from '@yummy/ui-rnr';

/**
 * Roughly how wide a name renders, counting a full-width character as two.
 *
 * Enough to decide whether two fit side by side: a column is a little under
 * half the sheet, and a name has to share it with a radio, a price and the
 * padding between them.
 */
function displayWidth(name: string): number {
  let width = 0;
  for (const character of name) {
    width += /[\u1100-\u115f\u2e80-\ua4cf\uac00-\ud7a3\uf900-\ufaff\ufe30-\ufe6f\uff00-\uff60\uffe0-\uffe6]/.test(character)
      ? 2
      : 1;
  }
  return width;
}

/** How wide a choice's name and its price may be and still sit two to a row. */
const TWO_UP_WIDTH = 12;

export interface MenuItemSheetProps {
  item: PublicMenuItem;
  currency: string;
  visible: boolean;
  onClose: () => void;
  /** Adds the configured dish, `quantity` times, to the order. */
  onAdd: (options: CartOption[], quantity: number) => void;
}

/**
 * Renders one dish's choices as a sheet.
 *
 * @param props The dish, its currency, visibility and the add handler.
 * @returns The sheet.
 */
export function MenuItemSheet({ item, currency, visible, onClose, onAdd }: MenuItemSheetProps) {
  const t = useT();
  const { isTablet } = useResponsive();

  // Memoised so the identity is stable: `option_groups` is always present but
  // the fallback `[]` would be a new array on every render, and `options` below
  // depends on it.
  const groups = useMemo(() => item.option_groups ?? [], [item.option_groups]);
  // The group whose choice *is* the price, if the dish has one. Only ever one:
  // a dish is sold by size or not at all.
  const priceGroup = groups.find((group) => group.price_mode === 'absolute');

  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  const [quantity, setQuantity] = useState(Math.max(1, item.min_quantity));

  // Fresh every time it opens. The cart is where a decision is kept, so a sheet
  // that remembered its last state would be offering choices the customer has
  // already made and cannot see.
  useEffect(() => {
    if (!visible) return;
    setChosen({});
    setQuantity(Math.max(1, item.min_quantity));
  }, [visible, item.id, item.min_quantity]);

  const toggle = useCallback(
    (group: PublicOptionGroup, choiceId: string) => {
      setChosen((current) => {
        const selected = current[group.id] ?? [];
        if (selected.includes(choiceId)) {
          return { ...current, [group.id]: selected.filter((id) => id !== choiceId) };
        }
        // One choice replaces; several accumulate to the group's limit. A
        // price-setting group is always the first kind — the schema makes it
        // single-select — so this needs no special case for sizes.
        if (group.max_select === 1) return { ...current, [group.id]: [choiceId] };
        if (selected.length >= group.max_select) return current;
        return { ...current, [group.id]: [...selected, choiceId] };
      });
    },
    []
  );

  /** Every choice made, in group order, as the cart stores a line's choices. */
  const options = useMemo<CartOption[]>(() => {
    const picked: CartOption[] = [];
    for (const group of groups) {
      for (const choiceId of chosen[group.id] ?? []) {
        const choice = group.choices.find((c) => c.id === choiceId);
        if (!choice) continue;
        picked.push({
          choiceId: choice.id,
          groupName: group.name,
          name: choice.name,
          priceCents: choice.price_cents,
          isAbsolute: group.price_mode === 'absolute',
          quantity: 1,
        });
      }
    }
    return picked;
  }, [groups, chosen]);

  const unitPrice = withOptions(item.effective_price_cents, options);

  // A group that must be answered before the dish can be ordered at all.
  const blocking = groups.find(
    (group) => group.min_select > 0 && (chosen[group.id] ?? []).length < group.min_select,
  );

  const priceChoice = priceGroup
    ? priceGroup.choices.find((choice) => (chosen[priceGroup.id] ?? []).includes(choice.id))
    : undefined;

  // The header states what the dish costs before the choices change it, or the
  // price the chosen size makes it. The button carries the running total: a
  // "base price" that moved as extras were added would be neither.
  const headerCents = priceChoice ? priceChoice.price_cents : item.effective_price_cents;
  const headerLabel = priceChoice ? priceChoice.name : t('customer.menu.basePrice');

  // What the total is made of. Past a couple of extras a sum is more readable
  // than a list, which is what a group of fifteen needs.
  const priced = options.filter((option) => !option.isAbsolute && option.priceCents > 0);
  const breakdown = [
    priceChoice
      ? `${priceChoice.name} ${formatMoney(priceChoice.price_cents, currency)}`
      : t('customer.menu.breakdownBase', { total: formatMoney(item.effective_price_cents, currency) }),
    priced.length <= 2
      ? priced.map((option) => `${option.name} ${formatMoney(option.priceCents, currency)}`).join(' + ')
      : t('customer.menu.breakdownExtras', {
          count: priced.length,
          total: formatMoney(
            priced.reduce((sum, option) => sum + option.priceCents, 0),
            currency
          ),
        }),
  ]
    .filter(Boolean)
    .join(' + ');

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      // Phone: up from the bottom, where a thumb already is. Wide: a centred
      // dialog, because a sheet pinned to the bottom of a wide window is both
      // stretched and easy to miss. The same rule the language sheet follows.
      placement={isTablet ? 'center' : 'bottom'}
      // A wide window has the room for a wider panel than the contents ask for,
      // and the choice rows are the whole point of the sheet.
      maxWidth={720}
      exactWidth={isTablet}
      title={item.name}
      footer={
        <Stack gap="sm">
          <Row gap="md" align="center">
            <Stepper
              value={quantity}
              onChange={setQuantity}
              min={Math.max(1, item.min_quantity)}
              max={99}
              floor={Math.max(1, item.min_quantity)}
            />
            <Button
              // Blocked, the button is the muted one rather than the primary
              // dimmed: `disabled` fades what is underneath it, which turned
              // white-on-orange into pale grey on pale orange and left the
              // group it is asking for unreadable — the one word that matters.
              className={cn('flex-1', blocking && 'bg-muted active:bg-muted opacity-100')}
              disabled={Boolean(blocking)}
              accessibilityLabel={
                blocking
                  ? t('customer.menu.chooseGroup', { group: blocking.name })
                  : t('customer.menu.addToOrder')
              }
              onPress={() => {
                if (blocking) return;
                onAdd(options, quantity);
                onClose();
              }}>
              <Row spread gap="sm" align="center" className="w-full">
                <Text
                  className={cn(
                    'text-sm font-semibold',
                    blocking ? 'text-muted-foreground' : 'text-primary-foreground',
                  )}>
                  {blocking
                    ? t('customer.menu.chooseGroup', { group: blocking.name })
                    : t('customer.menu.addToOrder')}
                </Text>
                {blocking ? null : (
                  <Text className="text-base font-bold">
                    {formatMoney(unitPrice * quantity, currency)}
                  </Text>
                )}
              </Row>
            </Button>
          </Row>
          <Text className="text-subtle-foreground text-center text-xs">{breakdown}</Text>
        </Stack>
      }>
      <Stack gap="lg">
        {item.description ? (
          <Text className="text-muted-foreground text-sm">{item.description}</Text>
        ) : null}

        <Row gap="sm" align="baseline">
          <Text className="text-lg font-bold">{formatMoney(headerCents, currency)}</Text>
          <Text className="text-muted-foreground text-sm">{headerLabel}</Text>
        </Row>

        {groups.map((group) => {
          const selected = chosen[group.id] ?? [];
          // Two to a row only while the words are short. A column is about
          // half the sheet, and a long name does not wrap there so much as
          // disappear: clamped at two lines it reads `Extra mus…`, which is
          // worse than a taller list. The decision is per group and made from
          // the group's own choices, so `tomato` and `garlic` still pair up
          // while a fifteen-character topping gets the row to itself.
          const fitsTwoUp = group.choices.every(
            (choice) =>
              displayWidth(choice.name) <= TWO_UP_WIDTH &&
              displayWidth(
                group.price_mode === 'absolute'
                  ? formatMoney(choice.price_cents, currency)
                  : choice.price_cents > 0
                    ? `+ ${formatMoney(choice.price_cents, currency)}`
                    : t('customer.menu.included'),
              ) <= TWO_UP_WIDTH,
          );
          // Two different questions. `radio` is what the group *is* — one
          // choice or several — and drives the control and what a screen reader
          // announces. `twoUp` is only how much room the words need.
          const radio = group.max_select === 1;
          const twoUp = radio && fitsTwoUp;
          return (
            <Stack key={group.id} gap="sm">
              <Row gap="sm" align="center">
                <Text className="text-base font-bold">{group.name}</Text>
                <Text className="text-muted-foreground text-sm">
                  {radio
                    ? t('customer.menu.chooseOne')
                    : t('customer.menu.pickUpTo', { count: group.max_select })}
                </Text>
                <View className="flex-1" />
                <ToneBadge
                  label={
                    group.min_select > 0
                      ? t('customer.menu.required')
                      : t('customer.menu.optional')
                  }
                  tone={group.min_select > 0 ? 'brand' : 'neutral'}
                  size="md"
                />
              </Row>

              {/* One choice goes in two columns, so a group of four is two rows
                  rather than four; several go full width, where the price has
                  room at the far end. */}
              <View className={cn('gap-2', twoUp || isTablet ? 'flex-row flex-wrap' : 'flex-col')}>
                {group.choices.map((choice) => {
                  const on = selected.includes(choice.id);
                  const label =
                    group.price_mode === 'absolute'
                      ? formatMoney(choice.price_cents, currency)
                      : choice.price_cents > 0
                        ? `+ ${formatMoney(choice.price_cents, currency)}`
                        : t('customer.menu.included');
                  return (
                    <Pressable
                      key={choice.id}
                      onPress={() => toggle(group, choice.id)}
                      accessibilityRole={radio ? 'radio' : 'checkbox'}
                      aria-checked={on}
                      accessibilityLabel={choice.name}
                      className={cn(
                        'flex-row items-center gap-2 rounded-md border px-3 py-2.5 active:opacity-80',
                        (twoUp || isTablet) && 'grow basis-[45%]',
                        on ? 'border-primary bg-primary-soft' : 'border-border',
                      )}>
                      <View
                        className={cn(
                          'h-4 w-4 flex-none items-center justify-center border',
                          radio ? 'rounded-full' : 'rounded',
                          on ? 'border-primary bg-primary' : 'border-subtle-foreground',
                        )}>
                        {on ? (
                          radio ? (
                            <View className="bg-primary-foreground h-1.5 w-1.5 rounded-full" />
                          ) : (
                            <Icon as={Check} size={12} className="text-primary-foreground" />
                          )
                        ) : null}
                      </View>
                      <Text
                        numberOfLines={2}
                        className={cn(
                          'min-w-0 text-base font-semibold',
                          on ? 'text-primary' : 'text-foreground',
                        )}>
                        {choice.name}
                      </Text>
                      <Text
                        // `shrink-0`: the price holds its width and the name
                        // wraps instead. Left shrinkable, a long name squeezed
                        // it into `+ SGD` above `2.00`.
                        className={cn(
                          'ml-auto shrink-0 text-sm',
                          on ? 'text-primary font-semibold' : 'text-muted-foreground',
                        )}>
                        {label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </Stack>
          );
        })}
      </Stack>
    </Modal>
  );
}
